import { QueryClient } from '@tanstack/react-query';

import { BOOKING_CALENDAR_OPTIONS_QUERY_KEY } from 'app/data/queries/use-booking-calendar-options-query';
import { BOOKINGS_QUERY_KEY } from 'app/data/queries/use-bookings-query';
import { EVENT_QUERY_KEY } from 'app/data/queries/use-event-query';
import { EVENT_SESSIONS_QUERY_KEY } from 'app/data/queries/use-event-sessions-query';

/**
 * Invalidate every cached query whose data reflects a user's bookings, so any
 * booking mutation (book / cancel / confirm / check-in) refreshes the UI
 * consistently:
 *
 * - bookings list — the user's own booking + status, which drives the CTA
 *   primary (BOOK vs Cancel vs Join waitlist) and the "Waitlisted" pills.
 * - event — `numberOfBookings` feeds the calendar block's capacity copy
 *   ("Only N spots left" / "Event at capacity") and the CTA's `isFull` /
 *   sold-out decision for single-session events.
 * - booking calendar options — the .ics carries the booking's times.
 * - event sessions — `cBookedCapacity` feeds the same capacity + `isFull`
 *   logic per slot.
 *
 * Invalidating only the bookings query (the previous behaviour) left the
 * calendar-block capacity and the BOOK-vs-Join-waitlist CTA reading stale
 * counts after a cancel: the booking disappeared, but "Event at capacity" and
 * the suppressed BOOK CTA persisted until a hard reload, because the event /
 * session counts were never refetched.
 */
export const invalidateBookingScopedQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [BOOKING_CALENDAR_OPTIONS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [EVENT_SESSIONS_QUERY_KEY] });
};
